import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, PageProps, Organization } from "../types/auth";

import styles from './TopBar.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface TopBarProps extends PageProps {
  onOrgChange?: (orgId: number) => void;
}

const TopBar: React.FC<TopBarProps> = ({ user, selectedOrgID, onOrgChange, setUser }) => {
  const navigate = useNavigate();
  const [selectedOrg, setSelectedOrg] = useState<number | string>(selectedOrgID || "");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showOrgDialog, setShowOrgDialog] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const orgDialogRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (orgDialogRef.current && !orgDialogRef.current.contains(event.target as Node)) {
        setShowOrgDialog(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?search_term=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm("");
    }
  };

  const handleOrgChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newOrgID = parseInt(event.target.value, 10);
    setSelectedOrg(newOrgID);
    localStorage.setItem("selectedOrgID", newOrgID.toString());
    onOrgChange?.(newOrgID);
  };

  const handleOrgSelect = (org: Organization) => {
    const newOrgID = org.org_id;
    setSelectedOrg(newOrgID);
    localStorage.setItem("selectedOrgID", newOrgID.toString());
    onOrgChange?.(newOrgID);
    setShowOrgDialog(false);
    setShowDropdown(false);
    // Navigate to home page after selecting an organization
    navigate("/home");
  };

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        console.error("No refresh token found. Logging out locally.");
        localStorage.clear();
        navigate("/login");
        return;
      }

      const token = localStorage.getItem("token");
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (err) {
      console.error("Failed to log out:", err);
    }

    localStorage.clear();
    navigate("/login");
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const openOrgDialog = () => {
    setShowOrgDialog(true);
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  const displayName = user.display_name === "" ? user.username : user.display_name;

  const selectedOrgDetails = user.organizations?.find((org) => org.org_id === selectedOrg);
  const selectedOrgName = selectedOrgDetails?.name || "No Organization";
  const isOrgOwner = selectedOrgDetails?.role === "owner";

  return (
    <div className={styles.topBar}>
      <div className={styles.logo} onClick={() => navigate("/home")} style={{ cursor: 'pointer' }}>
        <img 
          src="/img/buildyou_logo@3x.png" 
          alt="BuildYou" 
        />
      </div>
      <form onSubmit={handleSearch} className={styles.searchForm}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
          className={styles.searchInput}
        />
      </form>
      <div className={styles.userDropdownContainer} ref={dropdownRef}>
        <div className={styles.userDisplayName} onClick={toggleDropdown}>
          {displayName} ({selectedOrgName})
        </div>
        {showDropdown && (
          <div className={styles.dropdownMenu}>
            <div className={styles.dropdownItem} onClick={openOrgDialog}>
              Select Org
            </div>
            <div className={styles.dropdownDivider}></div>
            {isOrgOwner && (
              <>
                <div 
                  className={styles.dropdownItem} 
                  onClick={() => navigate('/settings')}
                >
                  Org Settings
                </div>
                <div className={styles.dropdownDivider}></div>
              </>
            )}
            <div className={styles.dropdownItem} onClick={() => navigate("/profile")}>
              Profile
            </div>
            <div className={styles.dropdownItem} onClick={handleLogout}>
              Logout
            </div>
          </div>
        )}
      </div>

      {showOrgDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.orgDialog} ref={orgDialogRef}>
            <div className={styles.orgDialogHeader}>
              <h3>Select Organization</h3>
              <button 
                className={styles.closeButton} 
                onClick={() => setShowOrgDialog(false)}
              >
                &times;
              </button>
            </div>
            <div className={styles.orgDialogContent}>
              {user.organizations && user.organizations.length > 0 ? (
                <ul className={styles.orgList}>
                  {user.organizations.map((org) => (
                    <li 
                      key={org.org_id} 
                      className={`${styles.orgItem} ${org.org_id === selectedOrg ? styles.selectedOrg : ''}`}
                      onClick={() => handleOrgSelect(org)}
                    >
                      <div className={styles.orgName}>{org.name}</div>
                      <div className={styles.orgRole}>{org.role}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.noOrgs}>You don't belong to any organizations.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopBar; 